import React, { useRef } from "react";
import { Alert, Box, Button, CircularProgress, Grid, InputAdornment, Typography } from "@mui/material";
import { CalendarMonth, Search } from "@mui/icons-material";
import moment from "moment";
import { CssTextField } from "../../components/TextField";
import { DATE, plural } from "../../services/moksha-reservations/format";
import { focusRing } from "./presentation";
import { CONTROL_HEIGHT, SectionTitle, SPINNER_SIZE, useColors } from "./ui";

const calendarIcon = {
    startAdornment: (
        <InputAdornment position="start">
            <CalendarMonth fontSize="small" />
        </InputAdornment>
    ),
};

function StayDates({ checkin, checkout, derived, checking, hasResults, hasLines, error, onPatch, onCheck }) {
    const colors = useColors();
    const checkinRef = useRef();
    const checkoutRef = useRef();
    const { dateErrors, datesValid, nightCount, stale } = derived;
    const needsCheck = !hasResults || stale;
    const dateInput = (key, label, ref, min) => (
        <CssTextField
            fullWidth
            required
            size="small"
            type="date"
            label={label}
            value={key === "checkin" ? checkin : checkout}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min }}
            onChange={(e) => onPatch({ [key]: e.target.value })}
            inputRef={ref}
            onClick={() => ref.current?.showPicker?.()}
            InputProps={calendarIcon}
            error={!!dateErrors[key]}
            helperText={dateErrors[key] || " "}
        />
    );

    return (
        <>
            <SectionTitle>Stay dates</SectionTitle>
            <Grid container spacing={2} alignItems="flex-start">
                <Grid item md={3} sm={6} xs={12}>
                    {dateInput("checkin", "Check-in", checkinRef, moment().format(DATE))}
                </Grid>
                <Grid item md={3} sm={6} xs={12}>
                    {dateInput(
                        "checkout",
                        "Check-out",
                        checkoutRef,
                        (checkin ? moment(checkin, DATE) : moment()).add(1, "day").format(DATE)
                    )}
                </Grid>
                <Grid item md={2} sm={6} xs={12}>
                    <Box display="flex" alignItems="center" height={CONTROL_HEIGHT}>
                        <Typography aria-live="polite" color={colors.grey[300]}>
                            {datesValid ? plural(nightCount, "night") : "— nights"}
                        </Typography>
                    </Box>
                </Grid>
                <Grid item md={4} sm={6} xs={12}>
                    <Button
                        fullWidth
                        variant={needsCheck ? "contained" : "outlined"}
                        color="secondary"
                        startIcon={checking ? null : <Search />}
                        disabled={!datesValid || checking}
                        onClick={onCheck}
                        sx={{ height: CONTROL_HEIGHT, ...focusRing(colors) }}
                    >
                        {checking ? (
                            <CircularProgress color="inherit" size={SPINNER_SIZE} />
                        ) : (
                            <b>{hasResults ? "Check again" : "Check availability"}</b>
                        )}
                    </Button>
                </Grid>
            </Grid>

            {error && (
                <Alert severity="error" sx={{ mt: 1 }}>
                    {error}
                </Alert>
            )}
            {stale && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                    The dates changed, so these results are out of date. Select "Check again" to refresh them.
                    {hasLines && " The nightly rates you entered will be cleared for the new dates."}
                </Alert>
            )}
        </>
    );
}

export default StayDates;
